import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@common/components/button';
import { Container } from '@common/components/container';
import ProfileImage from '@common/components/profile-image';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { EvaluationFormTypes, EPermissions } from '@common/definitions';
import CompetencyContainer from './comptency-container';
import QuestionContainer from './question-container';
import { useEvaluationStore, withEvaluationStore } from './store';
import Spinner from '@common/components/spinner';
import { useTranslation } from 'react-i18next';
import PageLeaveCheckPopup from '@common/components/confirm-button/page-leave-check-popup';
import { useHistory, useParams } from 'react-router';
import { TopNavigationBar } from '@common/components/navigation-bar';
import Skeleton from '@modules/performance/components/skeleton/skeleton';
import Icon from '@common/components/icon';
import { useAppSelector } from '@common/hooks';
import * as loggedUserSelector from '@modules/core/selectors/logged-user';
import ConfirmButton from '@common/components/confirm-button';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import LinkWrapper from '@common/components/linkWrapper';
import ContentModule from './content-module';
import { DrawerStoreProvider, useDrawerStore } from './drawer-store';
import EvaluationTab from './evaluation-tab';
import Tabs from '@common/components/tabs';
import Activity from './activity';
import Drawer from '@common/components/drawer/drawer';
import TalentReview from './talent-review';
import moment from 'moment';

const DrawerContent = ({
  evaluation,
  openSideBar,
  setOpenEvaluationSideBar,
}: any) => {
  const { t } = useTranslation();
  const { setOnCloseDrawer } = useDrawerStore();

  useEffect(() => {
    setOnCloseDrawer(() => setOpenEvaluationSideBar(false));
    return () => setOnCloseDrawer(undefined);
  }, [setOnCloseDrawer, setOpenEvaluationSideBar]);

  return (
    <Drawer
      show={openSideBar}
      onHide={() => setOpenEvaluationSideBar(false)}
      size="medium"
      className="tw-bg-gray-50 !tw-sticky tw-top-[200px] !tw-h-max-content !tw-z-9"
      hideCloseButton
      inline
    >
      <Tabs variant="full_width" headerItemClassName="!tw-pt-5 !tw-pb-5">
        <Tabs.Item title={t('performance:activity')}>
          <Activity userId={evaluation?.data?.reviewee?.id} />
        </Tabs.Item>
        <Tabs.Item title={t('performance:evaluation')}>
          <EvaluationTab currentEvaluationId={evaluation?.data?.id} />
        </Tabs.Item>
      </Tabs>
    </Drawer>
  );
};

const EvaluationForm = () => {
  const { t } = useTranslation();
  const { networkId } = useParams<{ networkId: string }>();
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const history = useHistory();
  const {
    evaluation,
    loading,
    submitEvaluation,
    isDirty,
    isSavingDraft,
    isSubmittingEvaluation,
    isCompletingEvaluation,
    completeEvaluation,
    openSideBar,
    setOpenEvaluationSideBar,
  } = useEvaluationStore();

  const loggedUser = useAppSelector(loggedUserSelector.selected);
  const permissions = useAppSelector(loggedUserSelector.permissions);
  const reviewer = evaluation?.data?.reviewers?.[0];
  const reviewerSubmitted = Boolean(reviewer?.status === 'submitted');
  const employeeVisibility = evaluation?.data?.form?.employee_visibility;
  const isReviewer =
    String(loggedUser?.id || '') === String(reviewer?.id || '');
  const isReviewee =
    String(loggedUser?.id || '') ===
    String(evaluation?.data?.reviewee?.id || '');
  const confirmDescription = employeeVisibility
    ? t('performance:employee_can_view_review_result')
    : t('performance:employee_cannot_view_review_result');

  // Modal state management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const revieweeId = String(evaluation?.data?.reviewee?.id || '');
  const handleStartChat = useCallback(() => {
    history.push(
      `${!networkId ? basePath : ''}/conversations/users/${
        isReviewee ? reviewer?.id : revieweeId
      }`
    );
  }, [basePath, isReviewee, reviewer, revieweeId, history]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalData(null);
  }, [setIsModalOpen, setModalData]);

  const handleModalSubmit = useCallback(() => {
    handleModalClose();
  }, [handleModalClose]);

  // New function to handle evaluation submission with modal callback
  const handleSubmitWithModal = async (operationData: any) => {
    await submitEvaluation('submitted', () => {
      if (formType === EvaluationFormTypes.MANAGER) {
        return;
      }

      if (isReviewer && !reviewerSubmitted) {
        setModalData(operationData);
        setIsModalOpen(true);
      } else if (isReviewee && !selfSubmitted) {
        setModalData(operationData);
        setIsModalOpen(true);
      }
    });
  };

  // Function to determine modal content based on submission states
  // Shows different content based on:
  // 1. Who is submitting (manager/employee)
  // 2. Whether the other party has already submitted
  const getModalContent = () => {
    if (!modalData) return null;

    // After user submits, determine what happens next based on current states
    const willManagerHaveSubmitted = isReviewer ? true : managerSubmitted;
    const willEmployeeHaveSubmitted = isReviewee ? true : selfSubmitted;
    const bothWillHaveSubmitted =
      willManagerHaveSubmitted && willEmployeeHaveSubmitted;

    if (isReviewer) {
      // Manager submitting
      if (bothWillHaveSubmitted) {
        // Manager submitting and employee HAS submitted
        return {
          icon: '/static/images/coffee_cup.png',
          title: t('performance:it_s_time_for_coffee'),
          message: t('performance:you_ve_both_finished_evaluation_manager'),
          buttons: [
            {
              buttons: [
                {
                  text: t('performance:close'),
                  type: 'inverted',
                  size: 'large',
                  fullWidth: true,
                  onClick: handleModalClose,
                },
                {
                  text: t('performance:start_chat'),
                  type: 'primary',
                  size: 'large',
                  fullWidth: true,
                  onClick: handleStartChat,
                },
              ],
            },
          ],
        };
      }
      // Manager submitting and employee HASN'T submitted
      return {
        icon: '/static/images/timer.png',
        title: t('performance:great_work_you_re_all_set'),
        message: t('performance:you_ve_completed_your_feedback_manager'),
        buttons: [
          {
            text: t('performance:i_understand'),
            type: 'primary',
            size: 'large',
            fullWidth: true,
            onClick: handleModalClose,
          },
        ],
      };
    }
    // Employee submitting
    if (bothWillHaveSubmitted) {
      // Employee submitting and manager HAS submitted
      return {
        icon: '/static/images/coffee_cup.png',
        title: t('performance:it_s_time_for_coffee'),
        message: t('performance:you_ve_both_finished_evaluation_employee'),
        buttons: [
          {
            buttons: [
              {
                text: t('performance:close'),
                type: 'inverted',
                size: 'large',
                fullWidth: true,
                onClick: handleModalClose,
              },
              {
                text: t('performance:start_chat'),
                type: 'primary',
                size: 'large',
                fullWidth: true,
                onClick: handleStartChat,
              },
            ],
          },
        ],
      };
    }
    // Employee submitting and manager HASN'T submitted
    return {
      icon: '/static/images/timer.png',
      title: t('performance:hang_tight_it_s_not_ready_yet'),
      message: t('performance:you_ve_completed_your_feedback_employee'),
      buttons: [
        {
          text: t('performance:i_understand'),
          type: 'primary',
          size: 'large',
          fullWidth: true,
          onClick: handleModalClose,
        },
      ],
    };
  };

  // Check if user is Organisation Admin
  const isOrganisationAdmin = permissions.includes(
    EPermissions.ORGANISATION_ORGANISATION_ADMINS_VIEW
  );
  const isEvaluationParticipant = isReviewer || isReviewee;
  const shouldHideActionButtons =
    isOrganisationAdmin && !isEvaluationParticipant;

  const getBackPath = () => {
    if (isReviewee) {
      return `${basePath}/performance/my-evaluations`;
    }

    return `${basePath}/performance/evaluations`;
  };

  const orderedComponents = useMemo(() => {
    const comps: any[] = evaluation?.data?.form?.components || [];
    const sorted = [...comps].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
    return sorted;
  }, [evaluation]);

  const selfSubmitted =
    evaluation?.data?.reviewee?.status === 'submitted' ||
    Boolean(evaluation?.data?.reviewee?.submitted_at);
  const managerSubmitted = Array.isArray(evaluation?.data?.reviewers)
    ? evaluation?.data?.reviewers.some(
        (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
      )
    : false;
  const bothSubmitted = Boolean(selfSubmitted && managerSubmitted);
  const isCompleted = Boolean(evaluation?.data?.completed_at);
  const formType = evaluation?.data?.form?.type;

  // Show end review button if:
  // 1. For manager_employee forms: Both manager and employee have submitted
  // 2. For manager forms: Only manager needs to submit
  const showEndReviewCta =
    isReviewer &&
    !isCompleted &&
    ((formType === EvaluationFormTypes.MANAGER_EMPLOYEE &&
      reviewerSubmitted &&
      selfSubmitted) ||
      (formType === EvaluationFormTypes.MANAGER && reviewerSubmitted));

  const isManagerType = formType === EvaluationFormTypes.MANAGER;

  return (
    <DrawerStoreProvider>
      <Container name="evaluation-form-details">
        <PageLeaveCheckPopup
          message={t('learning:gen_ai_unsaved_changes_description')}
          shouldRunOnRoute={() => isDirty}
          onSave={async () => submitEvaluation('draft')}
        />
        <TopNavigationBar
          className={`${openSideBar ? '!tw-z-10' : ''}`}
          breadcrumbs={[
            { name: t('performance:tab_performance') },
            {
              name: evaluation?.data?.reviewee?.full_name ? (
                evaluation?.data?.reviewee?.full_name
              ) : (
                <Skeleton height={16} width={120} />
              ),
              path: getBackPath(),
            },
            {
              name: evaluation?.data?.form?.title ? (
                evaluation?.data?.form?.title
              ) : (
                <Skeleton height={16} width={180} />
              ),
            },
          ]}
          title={
            evaluation?.data?.form?.title ? (
              evaluation?.data?.form?.title
            ) : (
              <Skeleton height={24} width={240} />
            )
          }
          employeeDetails={
            evaluation?.data ? (
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-my-3">
                <div
                  className="tw-flex tw-flex-row tw-text-gray-textDarkest
                      tw-text-base tw-font-medium tw-items-center tw-gap-1"
                >
                  <LinkWrapper
                    url={`${basePath}/users/${evaluation?.data?.reviewee.id}/about`}
                    className="tw-text-inherit hover:tw-text-inherit hover:tw-underline tw-flex tw-items-center tw-gap-1"
                  >
                    <>
                      <ProfileImage enablePopover size={20} user={evaluation?.data?.reviewee} />
                      {evaluation?.data?.reviewee?.full_name}
                    </>
                  </LinkWrapper>
                  <span className="tw-text-gray-text tw-text-sm tw-font-normal">
                    ({t('performance:only_employee')})
                  </span>
                  {!isManagerType && (
                  <Icon
                    type={selfSubmitted ? 'circle_check' : 'circle'}
                    size={14}
                    color={selfSubmitted ? 'green-success' : 'grey-dull'}
                  />
                  )}
                </div>
                <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />

                <div
                  className="tw-flex tw-flex-row tw-text-gray-textDarkest
                      tw-text-base tw-font-medium tw-items-center tw-gap-2"
                >
                  <LinkWrapper
                    url={`${basePath}/users/${evaluation?.data?.reviewers?.[0]?.id}/about`}
                    className="tw-text-inherit hover:tw-underline hover:tw-text-inherit tw-flex tw-items-center tw-gap-1"
                  >
                    <>
                      <ProfileImage
                        enablePopover
                        size={20}
                        user={evaluation?.data?.reviewers?.[0]}
                      />
                      {evaluation?.data?.reviewers?.[0]?.full_name}
                    </>
                  </LinkWrapper>
                  <span className="tw-text-gray-text tw-text-sm tw-font-normal">
                    ({t('performance:manager')})
                  </span>
                  <Icon
                    type={managerSubmitted ? 'circle_check' : 'circle'}
                    size={14}
                    color={managerSubmitted ? 'green-success' : 'grey-dull'}
                  />
                  <Icon type="pencil_edit" size={14} color="grey-dull" />
                </div>

                {evaluation?.data?.deadline_at && (
                  <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />
                )}
                {evaluation?.data?.deadline_at && (
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                    <Icon type="timer_default" size={16} color="red" />
                    <div className="tw-text-gray-textDarkest tw-text-base tw-font-medium">
                      {moment(evaluation?.data?.deadline_at).format(
                        'MMM D, YYYY'
                      )}
                    </div>
                    <Icon type="pencil_edit" size={14} color="grey-dull" />
                  </div>
                )}
              </div>
            ) : (
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-my-3">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                  <Skeleton circle height={20} width={20} />
                  <Skeleton height={16} width={140} />
                  <Skeleton height={12} width={80} />
                </div>
              </div>
            )
          }
          action={
            <>
              {!loading && (
                <Button
                  type="default"
                  size="large"
                  onClick={() => history.push(getBackPath())}
                >
                  {t('performance:go_back')}
                </Button>
              )}

              <Button
                type="default"
                icon="side_bar"
                size="large"
                iconColor="grey-dull"
                iconSize={18}
                className="tw-text-gray-text tw-text- tw-font-medium"
                onClick={() => setOpenEvaluationSideBar(!openSideBar)}
              >
                {openSideBar
                  ? t('performance:close_side_bar')
                  : t('performance:open_side_bar')}
              </Button>

              {!loading &&
                !shouldHideActionButtons &&
                !bothSubmitted &&
                !isCompleted &&
                (isReviewer ? !reviewerSubmitted : !selfSubmitted) && (
                  <Button
                    type="default"
                    size="large"
                    className="tw-mr-2"
                    isLoading={isSavingDraft}
                    onClick={async () => submitEvaluation('draft')}
                  >
                    {t('performance:save_as_draft')}
                  </Button>
                )}
              {!loading &&
                !shouldHideActionButtons &&
                !isCompleted &&
                (isReviewer ? (
                  <ConfirmButton
                    title=""
                    description={confirmDescription}
                    onCancel={() => {}}
                    onConfirm={() => handleSubmitWithModal({
                        action: 'submit',
                        evaluationId: evaluation?.data?.id,
                        reviewerName: reviewer?.full_name || 'Unknown Reviewer',
                        revieweeName:
                          evaluation?.data?.reviewee?.full_name ||
                          'Unknown Reviewee',
                        status: 'submitted',
                      })}
                    confirmText={
                      reviewerSubmitted
                        ? t('performance:save')
                        : t('performance:submit_review')
                    }
                    cancelText={t('performance:cancel')}
                    isLoading={isSubmittingEvaluation}
                  >
                    <Button
                      type="primary"
                      size="large"
                      className="tw-mr-2"
                      isLoading={isSubmittingEvaluation}
                      disabled={isSubmittingEvaluation}
                    >
                      {reviewerSubmitted
                        ? t('performance:save')
                        : t('performance:submit_review')}
                    </Button>
                  </ConfirmButton>
                ) : (
                  !selfSubmitted && (
                    <Button
                      type="primary"
                      size="large"
                      className="tw-mr-2"
                      isLoading={isSubmittingEvaluation}
                      disabled={isSubmittingEvaluation}
                      onClick={() => handleSubmitWithModal({
                          action: 'submit',
                          evaluationId: evaluation?.data?.id,
                          reviewerName:
                            reviewer?.full_name || 'Unknown Reviewer',
                          revieweeName:
                            evaluation?.data?.reviewee?.full_name ||
                            'Unknown Reviewee',
                          status: 'submitted',
                        })}
                    >
                      {t('performance:submit_review')}
                    </Button>
                  )
                ))}
              {!shouldHideActionButtons && showEndReviewCta && (
                <ConfirmButton
                  title=""
                  description={t('performance:end_the_review_description')}
                  onCancel={() => {}}
                  onConfirm={() => completeEvaluation()}
                  confirmText={t('performance:end_the_review')}
                  cancelText={t('performance:cancel')}
                  isLoading={isCompletingEvaluation}
                >
                  <Button
                    type="primary"
                    size="large"
                    className="tw-mr-2"
                    isLoading={isCompletingEvaluation}
                    disabled={isCompletingEvaluation}
                  >
                    {t('performance:end_the_review')}
                  </Button>
                </ConfirmButton>
              )}
            </>
          }
        />
        <div className="tw-flex tw-flex-row tw-justify-center tw-w-full tw-gap-0 tw-relative">
          <Container.Content
            className={`tw-min-w-0 tw-px-6 tw-py-4 !tw-max-w-[50%] ${
              openSideBar ? '!tw-flex !tw-flex-row !tw-justify-center' : ''
            }`}
          >
            {loading ? (
              <Spinner centered size="large" />
            ) : (
              <div
                className={`tw-flex tw-flex-col tw-gap-8 ${
                  openSideBar ? 'tw-w-[calc(100%-160px)]' : ''
                }`}
              >
                {orderedComponents.map((comp: any) => {
                  const type = String(comp?.type);
                  if (type === PerformanceQuestionType.COMPETENCIES) {
                    return (
                      <CompetencyContainer
                        key={String(comp?.id)}
                        evaluation={evaluation?.data}
                      />
                    );
                  }
                  if (type === PerformanceQuestionType.OPEN_QUESTION) {
                    return (
                      <QuestionContainer
                        key={String(comp?.id)}
                        componentId={String(comp?.id)}
                      />
                    );
                  }
                  if (type === PerformanceQuestionType.CONTENT_MODULE) {
                    return (
                      <ContentModule key={String(comp?.id)} component={comp} />
                    );
                  }
                  if (type === PerformanceQuestionType.TALENT_REVIEW) {
                    return (
                      <TalentReview
                        key={String(comp?.id)}
                        componentId={String(comp?.id)}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </Container.Content>

          <DrawerContent
            evaluation={evaluation}
            openSideBar={openSideBar}
            setOpenEvaluationSideBar={setOpenEvaluationSideBar}
          />
        </div>

        {/* Modal for dynamic data handling */}
        <ModalWindow
          show={isModalOpen}
          onHide={handleModalClose}
          size="small"
          className="HighfiveModalForm"
        >
          <ModalContent
            hideHeader
            hideFooter
            bodyClassName="!tw-p-8"
            onConfirm={handleModalSubmit}
          >
            {(() => {
              const content = getModalContent();
              if (!content) return null;

              return (
                <div className="tw-flex tw-flex-col tw-gap-6">
                  {/* Dynamic Icon */}
                  <div
                    className={`tw-flex tw-flex-row tw-items-center tw-justify-center 
                  tw-w-full tw-h-[200px]`}
                  >
                    <img src={content.icon} alt="Icon" />
                  </div>

                  {/* Dynamic Text Content */}
                  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
                    <div className="tw-text-2xl-plus tw-text-center  tw-font-semibold tw-text-gray-dark-900">
                      {content.title}
                    </div>
                    <div className="tw-text-lg tw-text-center tw-text-gray-textDark tw-font-normal">
                      {content.message}
                    </div>
                  </div>

                  {/* Dynamic Buttons */}
                  {content.buttons.map((buttonConfig: any) => {
                    if (buttonConfig.buttons) {
                      // Multiple buttons in a row
                      return (
                        <div
                          key={buttonConfig.text}
                          className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-3"
                        >
                          {buttonConfig.buttons.map((btn: any) => (
                            <Button
                              key={btn.text}
                              type={btn.type}
                              size={btn.size}
                              className={btn.fullWidth ? 'tw-w-full' : ''}
                              onClick={
                                btn.text === 'Close'
                                  ? handleModalClose
                                  : btn.onClick
                              }
                            >
                              {btn.text}
                            </Button>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <Button
                        key={buttonConfig.text}
                        type={buttonConfig.type}
                        size={buttonConfig.size}
                        className={buttonConfig.fullWidth ? 'tw-w-full' : ''}
                        onClick={buttonConfig.onClick}
                      >
                        {buttonConfig.text}
                      </Button>
                    );
                  })}
                </div>
              );
            })()}
          </ModalContent>
        </ModalWindow>
      </Container>
    </DrawerStoreProvider>
  );
};

export default withEvaluationStore(EvaluationForm);
